import {
  IngestionFieldPolicy,
  IngestedFinding,
  LocaleCode,
  LocalizationState,
  ReadinessFinding,
  SourceContent,
  TerminologyConcept,
  WebsiteEntry,
} from './types';
import { hashSource, nowIso } from './hash';

export const targetLocales: LocaleCode[] = ['ja', 'fr', 'de', 'es', 'pt-BR', 'ar'];

export const localeDisplayNames: Record<LocaleCode, string> = {
  en: 'English',
  ja: 'Japanese',
  fr: 'French',
  de: 'German',
  es: 'Spanish',
  'pt-BR': 'Brazilian Portuguese',
  ar: 'Arabic',
};

const source = (
  key: string,
  text: string,
  component: string,
  context: string,
  riskLevel: SourceContent['riskLevel'],
  maxLength?: number,
): SourceContent => {
  const time = nowIso();
  return {
    id: `source_${key.replace(/[^a-z0-9]/gi, '_')}`,
    key,
    sourceType: 'STATIC_UI',
    sourceText: text,
    sourceLocale: 'en',
    context,
    feature: component.includes('Scan') ? 'Scan Operations' : 'Exposure Management',
    component,
    riskLevel,
    createdAt: time,
    updatedAt: time,
    sourceHash: hashSource(text),
    maxLength,
  };
};

export const seededSources: SourceContent[] = [
  source('nav.exposure', 'Exposure Management', 'PrimaryNav', 'Navigation label for exposure management area', 'LOW', 32),
  source('scan.start', 'Start scan', 'ScanToolbar', 'Primary button that starts a new scan', 'LOW', 24),
  source('scan.delete', 'Delete scan', 'ScanDetails', 'Destructive button that permanently deletes a scan', 'HIGH', 24),
  source('finding.title', 'Critical vulnerabilities', 'FindingList', 'Finding section title for critical security issues', 'MEDIUM', 40),
  source('asset.risk', 'Asset Risk Score', 'AssetTable', 'Column heading for numeric exposure score', 'MEDIUM', 32),
  source(
    'help.remediation',
    'Review remediation guidance before applying changes.',
    'FindingDetails',
    'Workflow instruction shown before applying security remediation steps',
    'HIGH',
    90,
  ),
];

export const seededTerminology: TerminologyConcept[] = [
  {
    conceptId: 'FEATURE_EXPOSURE_MANAGEMENT',
    canonicalEnglish: 'Exposure Management',
    definition: 'Product area for finding, prioritizing, and reducing cyber exposure.',
    featureOwner: 'Exposure Management',
    status: 'APPROVED',
    version: 3,
    updatedAt: nowIso(),
    owner: 'Security PMM',
    terms: [
      { locale: 'ja', approved: 'エクスポージャー管理', aliases: ['露出管理'], forbidden: ['公開管理'], notes: 'Use product term consistently.' },
      { locale: 'fr', approved: 'Gestion de l’exposition', aliases: [], forbidden: ['Gestion exposition'], notes: '' },
      { locale: 'de', approved: 'Exposure Management', aliases: ['Expositionsverwaltung'], forbidden: ['Belichtungsverwaltung'], notes: 'English feature name approved.' },
      { locale: 'es', approved: 'Gestión de exposición', aliases: [], forbidden: ['Administración de exposición'], notes: '' },
      { locale: 'pt-BR', approved: 'Gerenciamento de exposição', aliases: [], forbidden: ['Gestão de exposição'], notes: '' },
      { locale: 'ar', approved: 'إدارة التعرض', aliases: [], forbidden: ['إدارة العرض'], notes: 'RTL approved product term.' },
    ],
  },
  {
    conceptId: 'FEATURE_EXPOSURE_VIEW',
    canonicalEnglish: 'Exposure View',
    definition: 'Visualization of cyber exposure across assets and findings.',
    featureOwner: 'Exposure Management',
    status: 'APPROVED',
    version: 2,
    updatedAt: nowIso(),
    owner: 'Exposure UX',
    terms: [
      { locale: 'ja', approved: 'エクスポージャービュー', aliases: [], forbidden: ['露出ビュー'], notes: '' },
      { locale: 'fr', approved: 'Vue d’exposition', aliases: [], forbidden: ['Vue exposition'], notes: '' },
      { locale: 'de', approved: 'Exposure-Ansicht', aliases: [], forbidden: ['Belichtungsansicht'], notes: '' },
      { locale: 'es', approved: 'Vista de exposición', aliases: [], forbidden: ['Vista exposición'], notes: '' },
      { locale: 'pt-BR', approved: 'Visualização de exposição', aliases: [], forbidden: ['Vista de exposição'], notes: '' },
      { locale: 'ar', approved: 'عرض التعرض', aliases: [], forbidden: ['عرض الإظهار'], notes: '' },
    ],
  },
  {
    conceptId: 'METRIC_ASSET_RISK_SCORE',
    canonicalEnglish: 'Asset Risk Score',
    definition: 'Numeric score representing risk associated with an asset.',
    featureOwner: 'Risk Analytics',
    status: 'APPROVED',
    version: 4,
    updatedAt: nowIso(),
    owner: 'Risk Platform',
    terms: [
      { locale: 'ja', approved: 'アセットリスクスコア', aliases: [], forbidden: ['資産危険点'], notes: '' },
      { locale: 'fr', approved: 'Score de risque de l’actif', aliases: [], forbidden: ['Note de risque actif'], notes: '' },
      { locale: 'de', approved: 'Asset-Risiko-Score', aliases: [], forbidden: ['Anlagenrisikowert'], notes: '' },
      { locale: 'es', approved: 'Puntuación de riesgo del activo', aliases: [], forbidden: ['Score de riesgo'], notes: '' },
      { locale: 'pt-BR', approved: 'Pontuação de risco do ativo', aliases: [], forbidden: ['Score de risco'], notes: '' },
      { locale: 'ar', approved: 'درجة مخاطر الأصل', aliases: [], forbidden: ['نقاط خطر الأصل'], notes: '' },
    ],
  },
  ...['Vulnerability', 'Remediation', 'Critical', 'Scan'].map((term, index) => ({
    conceptId: `SEC_${term.toUpperCase()}`,
    canonicalEnglish: term,
    definition: `${term} terminology used in cybersecurity workflows.`,
    featureOwner: 'Security Content',
    status: 'APPROVED' as const,
    version: 1,
    updatedAt: nowIso(),
    owner: 'Security Content',
    terms: [
      { locale: 'ja' as const, approved: ['脆弱性', '修復', '重大', 'スキャン'][index], aliases: [], forbidden: [`bad-${term}-ja`], notes: '' },
      { locale: 'fr' as const, approved: ['vulnérabilité', 'remédiation', 'critique', 'analyse'][index], aliases: [], forbidden: [`bad-${term}-fr`], notes: '' },
      { locale: 'de' as const, approved: ['Schwachstelle', 'Behebung', 'kritisch', 'Scan'][index], aliases: [], forbidden: [`bad-${term}-de`], notes: '' },
      { locale: 'es' as const, approved: ['vulnerabilidad', 'remediación', 'crítico', 'análisis'][index], aliases: [], forbidden: [`bad-${term}-es`], notes: '' },
      { locale: 'pt-BR' as const, approved: ['vulnerabilidade', 'remediação', 'crítico', 'verificação'][index], aliases: [], forbidden: [`bad-${term}-pt`], notes: '' },
      { locale: 'ar' as const, approved: ['ثغرة', 'معالجة', 'حرج', 'فحص'][index], aliases: [], forbidden: [`bad-${term}-ar`], notes: '' },
    ],
  })),
];

export const ingestionPolicy: IngestionFieldPolicy[] = [
  { field: 'cve', policy: 'NEVER_TRANSLATE', rationale: 'Authoritative security identifier.' },
  { field: 'hostname', policy: 'NEVER_TRANSLATE', rationale: 'Infrastructure entity name.' },
  { field: 'vendor', policy: 'NEVER_TRANSLATE', rationale: 'Legal/vendor name.' },
  { field: 'severity', policy: 'LOCALIZE_ENUM', rationale: 'Controlled enum with locale labels.' },
  { field: 'description', policy: 'TRANSLATE', rationale: 'Presentation-layer explanation.' },
  { field: 'remediation', policy: 'TRANSLATE_HIGH_RISK', rationale: 'Security guidance needs stricter review.' },
  { field: 'evidence', policy: 'PRESERVE_SOURCE', rationale: 'Command/path evidence must remain executable data, not prose.' },
];

export const sampleFinding: IngestedFinding = {
  cve: 'CVE-2026-12345',
  hostname: 'prod-db-07',
  vendor: 'ExampleVendor',
  severity: 'Critical',
  description: 'A remote code execution vulnerability affects the service.',
  remediation: 'Upgrade to version 4.2.1 and restart the affected service.',
  evidence: '/opt/example/bin/service --version',
};

export const websiteEntry: WebsiteEntry = {
  id: 'homepage-security',
  title: 'See and reduce cyber risk',
  body: 'Prioritize the exposures that matter most.',
  seoTitle: 'Cyber Exposure Management',
  slug: 'exposure-management',
};

export const demoScannerFindings: ReadinessFinding[] = [
  { file: 'src/demo-source/AssetTable.tsx', component: 'AssetTable', text: 'Delete asset', classification: 'probable user-facing hardcoded string', line: 18 },
  { file: 'src/demo-source/ScanToast.tsx', component: 'ScanToast', text: 'Scan complete', classification: 'probable user-facing hardcoded string', line: 9 },
  { file: 'src/demo-source/RiskOverview.tsx', component: 'RiskOverview', text: 'Risk overview', classification: 'probable user-facing hardcoded string', line: 12 },
  { file: 'src/demo-source/Telemetry.ts', component: 'Telemetry', text: 'scan_complete_event', classification: 'likely internal string', line: 5 },
  { file: 'src/demo-source/FindingBanner.tsx', component: 'FindingBanner', text: 'Review CVE-2026-12345 before applying fix', classification: 'uncertain', line: 22 },
];

export function createInitialLocalizationState(): LocalizationState {
  return {
    locales: [
      { code: 'en', displayName: 'English', direction: 'ltr', enabled: true, qualityThreshold: 0.9, autoPublishEnabled: true },
      ...targetLocales.map((code) => ({
        code,
        displayName: localeDisplayNames[code],
        direction: code === 'ar' ? 'rtl' as const : 'ltr' as const,
        enabled: true,
        qualityThreshold: code === 'ja' ? 0.86 : 0.84,
        autoPublishEnabled: true,
      })),
    ],
    sources: seededSources,
    localizations: [],
    terminology: seededTerminology,
    translationMemory: [
      {
        id: 'tm_seed_scan_start_fr',
        sourceHash: hashSource('Start scan'),
        sourceString: 'Start scan',
        locale: 'fr',
        translatedString: 'Démarrer l’analyse',
        origin: 'SEED',
        approvalStatus: 'APPROVED',
        usageCount: 2,
        qualityScore: 0.96,
        updatedAt: nowIso(),
      },
    ],
    qualityResults: [],
    reviewItems: [],
    auditEvents: [],
    pipelineStage: 'Idle',
    ingestedResults: [],
    assistantResults: [],
    websiteEntry,
    websiteVariants: [],
  };
}
